import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, Phone, Play, CheckCircle, ArrowRight, Star, Check,
  Globe, Code, Cloud, Brain, Users, Box, Smartphone,
  ShoppingCart, ScanLine, Bell, MessageSquare, HardDrive, FileJson,
  CreditCard, Sparkles, Search, BarChart3, Calculator,
} from 'lucide-react';
import { setSeoMetadata, generateOrganizationSchema, generateWebsiteSchema, SEO_PRESETS } from '../lib/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProjectEstimatorSection from '../components/ProjectEstimatorSection';
import ShowcaseDirectorySection from '../components/ShowcaseDirectorySection';
import FeaturedShowcaseSection from '../components/FeaturedShowcaseSection';


// Sectors we have shipped work in. Icons are reused from the previous build.
const INDUSTRIES = [
  { name: 'Construction', icon: '/images/icons/industries/indus-office.png' },
  { name: 'Finance', icon: '/images/icons/industries/indus-casino.png' },
  { name: 'Healthcare', icon: '/images/icons/industries/indus-hospital.png' },
  { name: 'Education', icon: '/images/icons/industries/indus-school.png' },
  { name: 'Hospitality', icon: '/images/icons/industries/indus-bed.png' },
  { name: 'Retail', icon: '/images/icons/industries/indus-mall.png' },
  { name: 'Restaurants', icon: '/images/icons/industries/indus-restaurant.png' },
  { name: 'Logistics', icon: '/images/icons/industries/indus-airport.png' },
  { name: 'Fitness', icon: '/images/icons/industries/indus-gym.png' },
  { name: 'Care Services', icon: '/images/icons/industries/indus-wheelchair.png' },
  { name: 'Professional Services', icon: '/images/icons/industries/indus-museum.png' },
  { name: 'Field Service', icon: '/images/icons/industries/indus-janitor.png' },
];

// Track-record stats shown under the hero.
const STATS = [
  { value: '50+', label: 'Projects Delivered' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '20+', label: 'Years Experience' },
  { value: '24/7', label: 'Support Available' },
];

// Broad service-line overview ("What We Do").
const CAPABILITIES = [
  { title: 'CMS Web Design', description: 'Beautiful, responsive websites built on powerful content management systems for easy updates.', icon: Globe },
  { title: 'Custom Web Applications', description: 'Full-stack web applications tailored to your business needs with modern technologies.', icon: Code },
  { title: 'SaaS Platform Development', description: 'Multi-tenant software solutions with subscription management and white-label options.', icon: Cloud },
  { title: 'AI & Machine Learning', description: 'Intelligent applications powered by the latest AI models including GPT-4o and DALL-E 3.', icon: Brain },
  { title: 'CRM Systems', description: 'Custom customer relationship management solutions with full pipeline tracking.', icon: Users },
  { title: '3D Visualization', description: 'Interactive 3D configurators and visualization tools for product customization.', icon: Box },
  { title: 'Mobile-First Design', description: 'Responsive applications that work flawlessly across all devices and screen sizes.', icon: Smartphone },
];

// Interactive "Features We Can Build For You" showcase - one detail panel per
// feature, same content on desktop (click-to-expand) and mobile (card grid).
const BUILD_FEATURES = [
  {
    id: 'shopping-cart',
    icon: ShoppingCart,
    title: 'Custom Shopping Cart',
    description: "Transform your business with a fully customizable e-commerce solution built from the ground up. Our shopping cart systems include comprehensive inventory management with real-time stock tracking, flexible product variants for size, color, and custom options, powerful discount and coupon code engines, and seamless checkout experiences that convert browsers into buyers. We integrate with multiple payment providers and shipping carriers to give your customers the flexibility they expect.\n\nOur e-commerce solutions also feature abandoned cart recovery, customer wishlists, product reviews and ratings, related product recommendations, and advanced analytics to track your sales funnel. Whether you're selling physical products, digital downloads, or subscription services, we build systems that scale with your business and provide the tools you need to maximize revenue.",
    highlights: ['Product Management', 'Inventory Tracking', 'Discount Codes', 'Multi-Currency'],
  },
  {
    id: 'ocr',
    icon: ScanLine,
    title: 'OCR & Image Reading',
    description: 'Harness the power of advanced optical character recognition technology to automatically extract text and data from images, receipts, invoices, business cards, and documents. Our OCR solutions eliminate manual data entry, reduce errors, and save countless hours of administrative work. We support multiple languages, handwriting recognition, and can be trained to understand your specific document formats for maximum accuracy and efficiency.\n\nBeyond basic text extraction, our systems can intelligently categorize and route documents, extract specific fields like totals, dates, and vendor names, and integrate directly with your accounting or inventory systems. We implement validation rules to catch errors before they enter your database, and provide confidence scores so you know when human review is needed.',
    highlights: ['Receipt Scanning', 'Document Parsing', 'Data Extraction', 'Multi-Language'],
  },
  {
    id: 'push-notifications',
    icon: Bell,
    title: 'Push Notifications',
    description: 'Keep your users engaged and informed with real-time push notifications across web and mobile platforms. Our notification systems support instant alerts, scheduled messages, and intelligent targeting based on user behavior and preferences. Segment your audience, A/B test your messaging, and track engagement analytics to continuously optimize your communication strategy and drive user retention.\n\nWe implement rich notifications with images, action buttons, and deep links that take users exactly where they need to go. Our systems include quiet hours respect, frequency capping to prevent notification fatigue, and preference centers so users stay in control. Whether you\'re sending order updates, promotional offers, or critical alerts, your messages will reach the right people at the right time.',
    highlights: ['Real-time Alerts', 'Scheduled Messages', 'User Segments', 'Analytics'],
  },
  {
    id: 'team-chat',
    icon: MessageSquare,
    title: 'Team Chat & Messaging',
    description: 'Empower your team with built-in communication tools that rival Slack and Microsoft Teams. Our messaging solutions include organized channels for different projects or departments, direct messaging for private conversations, seamless file sharing with preview capabilities, searchable message history, and real-time presence indicators. Keep your team connected and productive without switching between multiple applications.\n\nAdvanced features include threaded conversations to keep discussions organized, @mentions and notifications, emoji reactions, message editing and deletion, and pinned messages for important announcements. We can integrate with your existing tools through webhooks and bots, automate routine communications, and provide admin controls for message retention and compliance requirements.',
    highlights: ['Group Channels', 'Direct Messages', 'File Sharing', 'Message History'],
  },
  {
    id: 'storage',
    icon: HardDrive,
    title: 'Cloud Storage Buckets',
    description: 'Secure, scalable cloud storage solutions that grow with your business. We implement enterprise-grade file storage with automatic CDN delivery for lightning-fast access worldwide, granular access controls to protect sensitive data, automatic backup and version history, and support for any file type including images, videos, documents, and large datasets. Your files are encrypted at rest and in transit for maximum security.\n\nOur storage solutions include intelligent image processing with automatic thumbnails, format conversion, and optimization for web delivery. We implement virus scanning on uploads, quota management, and detailed usage analytics. Whether you need simple file attachments or complex media libraries with tagging and search, we build storage systems that handle it all effortlessly.',
    highlights: ['Secure Upload', 'CDN Delivery', 'Access Control', 'Auto Backup'],
  },
  {
    id: 'import-export',
    icon: FileJson,
    title: 'Import/Export Features',
    description: 'Seamlessly move data in and out of your application with comprehensive import and export functionality. We support all major formats including JSON, CSV, Excel, XML, and custom formats specific to your industry. Bulk operations handle thousands of records efficiently, with validation, error handling, and progress tracking. Migrate from legacy systems or integrate with external tools without data loss or formatting issues.\n\nOur import systems include field mapping interfaces, duplicate detection, and rollback capabilities if something goes wrong. Export features support scheduled reports, custom templates, and direct delivery to email or cloud storage. We can build automated data pipelines that sync your application with external systems on a schedule, keeping everything in perfect harmony.',
    highlights: ['JSON Export', 'CSV Import', 'Excel Support', 'Bulk Operations'],
  },
  {
    id: 'stripe',
    icon: CreditCard,
    title: 'Stripe Payment Gateway',
    description: 'Accept payments with confidence using our secure Stripe integration. We implement the full suite of Stripe capabilities including recurring subscriptions with flexible billing cycles, one-time payments with saved payment methods, professional invoicing with automatic reminders, and seamless refund processing. PCI compliance is handled automatically, and your customers enjoy a smooth, trustworthy checkout experience that maximizes conversions.\n\nAdvanced payment features include metered billing for usage-based pricing, proration for mid-cycle changes, dunning management to recover failed payments, and detailed revenue analytics. We support multiple currencies, local payment methods, and can implement complex pricing models with tiers, add-ons, and trial periods. Your finance team will love the automated reconciliation and reporting capabilities.',
    highlights: ['Subscriptions', 'One-time Payments', 'Invoicing', 'Refunds'],
  },
  {
    id: 'ai',
    icon: Sparkles,
    title: 'AI Functionality',
    description: 'Stay ahead of the competition with cutting-edge artificial intelligence integration. We implement GPT-4 and other leading AI models for intelligent content generation, customer service chatbots that handle inquiries 24/7, image generation and analysis, predictive analytics, and workflow automation that learns and improves over time. Our AI solutions are practical, cost-effective, and designed to deliver immediate business value.\n\nWe specialize in fine-tuning AI for your specific use case, whether that\'s understanding industry terminology, matching your brand voice, or learning from your historical data. Our implementations include usage monitoring and cost controls, fallback handling for reliability, and continuous improvement based on user feedback. From simple text summarization to complex decision support systems, we make AI accessible and useful.',
    highlights: ['GPT-4 Integration', 'Image Generation', 'Smart Chatbots', 'Automation'],
  },
  {
    id: 'seo',
    icon: Search,
    title: 'SEO Optimization',
    description: 'Dominate search engine rankings with built-in SEO tools that handle the technical details automatically. We implement dynamic meta tags, automatic sitemap generation, structured data markup for rich snippets, canonical URLs, and performance optimization for Core Web Vitals. Our SEO-first approach ensures your application is discoverable from day one, driving organic traffic and reducing your marketing costs.\n\nBeyond technical SEO, we build content management features that guide your team toward SEO best practices, including keyword density indicators, readability scores, and internal linking suggestions. Our systems generate SEO reports showing your ranking progress, identify opportunities for improvement, and integrate with Google Search Console and Analytics for comprehensive insights.',
    highlights: ['Meta Tags', 'Sitemaps', 'Schema Markup', 'Analytics'],
  },
  {
    id: 'collaboration',
    icon: Users,
    title: 'Collaboration Tools',
    description: "Enable seamless teamwork with real-time collaboration features that keep everyone on the same page. Our solutions include shared workspaces where multiple users can work simultaneously, threaded comments and discussions attached to any content, comprehensive activity feeds that track all changes, and flexible permission systems that give the right access to the right people. Perfect for remote teams and distributed organizations.\n\nWe implement real-time cursors and presence indicators so you can see who's working on what, conflict resolution for simultaneous edits, and notification preferences so team members stay informed without being overwhelmed. Version history lets you see who changed what and when, with the ability to compare and restore previous versions. Build a culture of transparency and accountability with tools that make collaboration effortless.",
    highlights: ['Shared Workspaces', 'Comments', 'Activity Feed', 'Permissions'],
  },
  {
    id: 'crm',
    icon: BarChart3,
    title: 'Custom CRM Solutions',
    description: 'Stop forcing your business into generic CRM templates. We build tailored customer relationship management systems designed around your specific workflows, sales processes, and industry requirements. Track leads from first contact to closed deal with customizable pipelines, create unlimited custom fields to capture the data that matters to you, and generate insightful reports that drive better business decisions.\n\nOur CRM solutions include email integration that logs all communications automatically, task management with reminders and assignments, calendar scheduling with availability sharing, and document generation for proposals and contracts. We build dashboards that give sales managers real-time visibility into team performance, forecast accuracy, and pipeline health. Your CRM should work the way you work, not the other way around.',
    highlights: ['Lead Management', 'Sales Pipeline', 'Custom Fields', 'Reporting'],
  },
  {
    id: 'accounting',
    icon: Calculator,
    title: 'Accounting Features',
    description: 'Take control of your finances with comprehensive accounting tools built into your application. We implement professional invoicing with customizable templates and automatic payment reminders, expense tracking with receipt capture, profit and loss statements, balance sheets, and tax-ready reports that make year-end a breeze. Integration with popular accounting software like QuickBooks ensures your data flows seamlessly between systems.\n\nAdvanced features include multi-currency support with automatic exchange rate updates, recurring invoice automation, late payment fees and interest calculations, and detailed aging reports. We build approval workflows for expenses, budget tracking with alerts, and financial dashboards that give you instant insight into your business health. From freelancers to growing businesses, our accounting features provide the financial clarity you need to succeed.',
    highlights: ['Invoicing', 'Expense Tracking', 'Financial Reports', 'Tax Ready'],
  },
];

// Client quotes.
const TESTIMONIALS = [
  { name: 'Michael Rodriguez', role: 'CEO', company: 'TechVenture Solutions', location: 'Austin, TX', content: 'A2G Designs transformed our entire business operations with their custom CRM solution. The attention to detail and understanding of our workflow was exceptional. Highly recommend!' },
  { name: 'Sarah Chen', role: 'Marketing Director', company: 'Coastal Innovations', location: 'San Diego, CA', content: 'The AI content platform they built for us has saved countless hours of work. The team at A2G truly understands modern technology and delivers enterprise-quality solutions.' },
  { name: 'James Patterson', role: 'Operations Manager', company: 'BuildRight Construction', location: 'Denver, CO', content: 'Their 3D configurator completely revolutionized how we sell metal buildings. Clients love the interactive experience, and our sales have increased 40% since launch.' },
  { name: 'Emily Thompson', role: 'Founder', company: 'Digital First Agency', location: 'Miami, FL', content: 'Working with A2G Designs was a game-changer for our agency. Their project management platform keeps our team organized and clients happy. Affordable and professional!' },
  { name: 'David Kim', role: 'Investment Advisor', company: 'Pacific Wealth Management', location: 'Seattle, WA', content: 'The stock tracking platform they developed is phenomenal. Clean interface, powerful features, and the AI integration is incredibly accurate. Worth every penny.' },
  { name: 'Rachel Martinez', role: 'Small Business Owner', company: 'Sunshine Accounting', location: 'Phoenix, AZ', content: 'Their receipt management app has simplified my bookkeeping tremendously. Simple to use but powerful enough for all my business needs. Thank you A2G!' },
];

const FAQS = [
  { question: 'What types of software do you develop?', answer: 'We specialize in custom web applications, SaaS platforms, CRM systems, AI-powered tools, 3D configurators, and mobile-responsive applications. Our expertise spans across various industries including finance, construction, marketing, and project management.' },
  { question: 'How long does a typical project take?', answer: 'Project timelines vary based on complexity. Simple applications may take 4-8 weeks, while comprehensive SaaS platforms can take 3-6 months. We provide detailed timelines during our initial consultation and keep you updated throughout the development process.' },
  { question: 'What is your pricing structure?', answer: 'We offer competitive, transparent pricing tailored to your project scope. Our rates are significantly lower than large agencies while maintaining enterprise-level quality. Contact us for a free consultation and custom quote based on your specific needs.' },
  { question: 'Do you provide ongoing support and maintenance?', answer: 'Absolutely! We offer various support packages including bug fixes, feature updates, security patches, and performance optimization. We believe in long-term partnerships with our clients.' },
  { question: 'What technologies do you work with?', answer: "We work with modern technologies including React, Node.js, Python, FastAPI, MongoDB, PostgreSQL, AWS, and cutting-edge AI APIs like OpenAI's GPT-4o and DALL-E 3. We choose the best tech stack for each project's unique requirements." },
  { question: 'Can you work with existing systems?', answer: "Yes! We can integrate with your existing software, APIs, and databases. Whether you need to enhance current functionality or build new features that connect to your systems, we've got you covered." },
  { question: 'Do you offer white-label solutions?', answer: 'Yes, we specialize in white-label SaaS platforms. Your clients will see your branding, not ours. This includes custom domains, logos, color schemes, and fully branded user experiences.' },
  { question: 'How do we get started?', answer: "Simply reach out through our contact form or give us a call. We'll schedule a free consultation to discuss your project needs, timeline, and budget. From there, we'll provide a detailed proposal and get started on bringing your vision to life." },
];

// How we build - the capabilities that go into every engagement.
const TECH_FEATURES = [
  {
    title: 'Built on your data, not a template',
    description: 'Your workflow decides the data model, not the other way round. Integrations with the systems you already pay for come as standard, not as an upsell.',
  },
  {
    title: 'Yours to own and move',
    description: 'Standard frameworks, documented and handed over. No proprietary runtime, no per-seat licence on your own software, and nothing stopping you taking it elsewhere.',
  },
  {
    title: 'Shipped in stages you can see',
    description: 'Work goes live in increments you can use and judge, so scope can change while it is still cheap to change. No six-month gap between kickoff and the first working screen.',
  },
];

// Shown on mobile in place of the hero video, and as the video's poster on
// desktop so there is never a black frame while it buffers.
const HERO_POSTER = '/images/home/hero-data-center.jpg';

// Below this width the hero video is not rendered at all.
const HERO_VIDEO_MIN_WIDTH = 768;

const HomePage = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activeBuildFeature, setActiveBuildFeature] = useState(BUILD_FEATURES[0]);
  const [openFaq, setOpenFaq] = useState(null);

  /**
   * Whether to render the hero video.
   *
   * Starts false so the very first paint never queues a 7.2 MB download on a
   * phone — if the check ran optimistically the request would already be in
   * flight by the time we corrected it. Also respects prefers-reduced-motion:
   * an autoplaying full-bleed video is exactly what that setting is for.
   */
  const [showHeroVideo, setShowHeroVideo] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const query = window.matchMedia(
      `(min-width: ${HERO_VIDEO_MIN_WIDTH}px) and (prefers-reduced-motion: no-preference)`,
    );
    const apply = () => setShowHeroVideo(query.matches);
    apply();

    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    // Set SEO metadata
    // The preset already carries title/description/keywords, and
    // setSeoMetadata appends the configured business name to the title. The
    // overrides that used to sit here were what kept the old brand in the
    // homepage <title> after the rebrand.
    setSeoMetadata({
      ...SEO_PRESETS.home,
      jsonLd: [generateOrganizationSchema(), generateWebsiteSchema()],
    });
  }, []);

  const scrollToDemo = () => {
    document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-bots-dark">
      <Header />
      
      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background — desktop only.
            The <video> is not rendered at all below the breakpoint rather than
            hidden with CSS: a hidden video still downloads the full file, which
            is 7.2 MB of mobile data before anything on the page appears. Small
            screens get the poster image as a plain background instead. */}
        {showHeroVideo ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            poster={HERO_POSTER}
          >
            <source src="/videos/home/data-center-hero.mp4" type="video/mp4" />
          </video>
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_POSTER})` }}
            aria-hidden="true"
          />
        )}
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-bots-dark/80 via-bots-dark/60 to-bots-dark" />
        
        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <p className="text-[#FF6B00] font-semibold mb-4 animate-fade-in-down text-sm tracking-[0.2em]">
            CUSTOM SOFTWARE SOLUTIONS
          </p>

          {/* The business name comes from General Settings, never hardcoded. */}
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in-down">
            Custom software deployment{' '}
            <span className="text-[#FF6B00]">at an affordable cost.</span>
          </h1>

          <p className="text-[#dfddd6] text-lg md:text-xl mb-10 max-w-3xl mx-auto leading-relaxed animate-fade-in">
            We build powerful, scalable software that transforms how businesses run &mdash; from
            AI-powered platforms to custom CRM systems, delivered at prices that make sense for
            small and mid-sized companies.
          </p>

          {/* Primary actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6 animate-fade-in">
            <Link
              to="/apps"
              className="px-8 py-4 bg-[#FF6B00] hover:bg-[#ff8124] text-[#1a1c1b] font-bold rounded-full text-lg transition-all transform hover:scale-105 shadow-lg shadow-[#FF6B00]/30 w-full sm:w-auto"
              data-testid="hero-cta-work"
            >
              View our work
            </Link>

            <Link
              to="/contact"
              className="px-8 py-4 border-2 border-[#dfddd6]/40 hover:border-[#FF6B00] text-white hover:text-[#FF6B00] font-bold rounded-full text-lg transition-all w-full sm:w-auto"
              data-testid="hero-cta-quote"
            >
              Get a free quote
            </Link>
          </div>

          <p className="text-[#888680] text-sm mb-8 animate-fade-in">
            Or scroll down to estimate your project
          </p>
          
          {/* Scroll Down Indicator */}
          <div className="animate-bounce-slow">
            <img 
              src="/images/home/scroll-down.gif" 
              alt="Scroll down" 
              className="w-12 h-12 mx-auto opacity-70"
            />
          </div>
        </div>
      </section>

      {/* Track record */}
      <section className="py-12 bg-bots-surface border-y border-[#4a3520]">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map((stat) => (
            <div key={stat.label}>
              <p className="text-3xl md:text-4xl font-bold text-[#FF6B00]">{stat.value}</p>
              <p className="text-[#888680] text-sm mt-1 tracking-wide uppercase">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Facility Automation Configurator Section */}
      <ShowcaseDirectorySection />

      <ProjectEstimatorSection />

      {/* Featured client spotlight — starred entries from the showcase
          directory. Replaces the robot product carousel that used to sit
          here, along with its 7.2MB background video. */}
      <FeaturedShowcaseSection />

      {/* Features we can build for you - interactive on desktop (click a
          feature, read its detail panel), a plain card grid on mobile. */}
      <section className="py-20 bg-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-semibold tracking-wide mb-4">
              POWERFUL CAPABILITIES
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Features We Can Build For You
            </h2>
            <p className="text-[#888680] text-lg">
              Enterprise-level features at small business prices. We integrate powerful
              functionality into every application we build, giving you the tools to
              compete with the big players.
            </p>
          </div>

          {/* Desktop: click-to-expand list + sticky detail panel */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5 space-y-2">
              {BUILD_FEATURES.map((feature) => {
                const Icon = feature.icon;
                const isActive = activeBuildFeature.id === feature.id;
                return (
                  <button
                    key={feature.id}
                    type="button"
                    onClick={() => setActiveBuildFeature(feature)}
                    className={`w-full text-left p-4 rounded-lg flex items-center gap-4 transition-all duration-300 group ${
                      isActive
                        ? 'bg-[#FF6B00] text-[#1a1c1b]'
                        : 'bg-bots-surface/50 border border-[#4a3520] text-white hover:border-[#FF6B00]'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                      isActive ? 'bg-[#1a1c1b]/20' : 'bg-[#4a3520] group-hover:bg-[#FF6B00]/20'
                    }`}>
                      <Icon size={20} className={isActive ? 'text-[#1a1c1b]' : 'text-[#FF6B00]'} />
                    </div>
                    <span className="font-semibold">{feature.title}</span>
                    <ArrowRight size={16} className={`ml-auto transition-transform ${isActive ? 'translate-x-1 text-[#1a1c1b]' : 'text-[#888680]'}`} />
                  </button>
                );
              })}
            </div>

            <div className="lg:col-span-7">
              <div className="bg-bots-surface/50 border border-[#4a3520] rounded-2xl p-8 h-full sticky top-32">
                <div className="w-16 h-16 bg-[#FF6B00] rounded-xl flex items-center justify-center mb-6">
                  <activeBuildFeature.icon size={32} className="text-[#1a1c1b]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{activeBuildFeature.title}</h3>
                <div className="text-[#888680] text-lg leading-relaxed mb-8 space-y-4">
                  {activeBuildFeature.description.split('\n\n').map((paragraph) => (
                    <p key={paragraph.slice(0, 24)}>{paragraph}</p>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {activeBuildFeature.highlights.map((highlight) => (
                    <div key={highlight} className="flex items-center gap-3 bg-bots-dark p-3 rounded-lg">
                      <div className="w-6 h-6 bg-[#FF6B00] rounded-full flex items-center justify-center shrink-0">
                        <Check size={14} className="text-[#1a1c1b]" />
                      </div>
                      <span className="text-white text-sm font-medium">{highlight}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF6B00] hover:bg-[#ff8124] text-[#1a1c1b] font-bold rounded-full transition-colors"
                >
                  Get This Feature
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>

          {/* Mobile/tablet: plain card grid, no interactivity needed */}
          <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-6">
            {BUILD_FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.id} className="bg-bots-surface/50 border border-[#4a3520] rounded-xl p-6">
                  <div className="w-12 h-12 bg-[#4a3520] rounded-lg flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#FF6B00]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-[#888680] text-sm mb-4 line-clamp-3">{feature.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {feature.highlights.slice(0, 2).map((highlight) => (
                      <span key={highlight} className="text-xs bg-[#4a3520]/50 text-[#FF6B00] px-2 py-1 rounded">
                        {highlight}
                      </span>
                    ))}
                    <span className="text-xs text-[#888680]">+{feature.highlights.length - 2} more</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-16">
            <div className="inline-flex items-center gap-3 bg-bots-surface/50 border border-[#4a3520] rounded-full px-6 py-3 mb-6">
              <Sparkles size={18} className="text-[#FF6B00]" />
              <span className="text-white font-medium">Affordable for Small Businesses</span>
            </div>
            <p className="text-[#888680] max-w-2xl mx-auto mb-8">
              Don&apos;t let budget constraints hold you back. We build enterprise-grade
              features at prices small businesses can afford. Let&apos;s discuss your
              project today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF6B00] hover:bg-[#ff8124] text-[#1a1c1b] font-bold rounded-full text-lg transition-colors"
              >
                Request a Quote
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/apps"
                className="px-8 py-4 border-2 border-[#dfddd6]/40 hover:border-[#FF6B00] text-white hover:text-[#FF6B00] font-bold rounded-full text-lg transition-all"
              >
                See Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What we do - broad service-line overview */}
      <section className="py-20 bg-gradient-to-r from-[#4a3520]/30 to-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Our Capabilities
          </h2>
          <p className="text-[#888680] text-center max-w-2xl mx-auto mb-12">
            We specialize in building comprehensive software solutions that drive business
            growth and digital transformation.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CAPABILITIES.map((capability) => (
              <div key={capability.title} className="bg-bots-surface/50 p-6 rounded-xl border border-[#4a3520]">
                <div className="w-11 h-11 rounded-lg bg-[#FF6B00]/10 flex items-center justify-center mb-4">
                  <capability.icon size={22} className="text-[#FF6B00]" />
                </div>
                <h3 className="text-white font-bold mb-2">{capability.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we build */}
      <section className="py-20 bg-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4">
            Software that fits how you actually work
          </h2>
          <p className="text-[#888680] text-center max-w-2xl mx-auto mb-12">
            Most businesses outgrow off-the-shelf tools long before they replace them. The
            workarounds become the process, and the process becomes the limit.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-bots-surface/50 p-8 rounded-2xl border border-[#4a3520]">
              <h3 className="text-2xl font-bold text-white mb-4">Custom platforms</h3>
              <p className="text-gray-300 leading-relaxed">
                Client portals, booking and scheduling, inventory, quoting, internal dashboards &mdash;
                built around your workflow rather than bent to fit someone else&apos;s. One system
                your team actually opens, instead of five they tolerate.
              </p>
            </div>

            <div className="bg-bots-surface/50 p-8 rounded-2xl border border-[#4a3520]">
              <h3 className="text-2xl font-bold text-white mb-4">Integration and automation</h3>
              <p className="text-gray-300 leading-relaxed">
                Connect the tools you already pay for so data stops being rekeyed between them.
                The repetitive work that quietly eats hours every week is usually the cheapest
                thing to automate and the first thing worth doing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-20 bg-gradient-to-r from-[#4a3520]/30 to-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Scope it before you commit to it
              </h2>
              <p className="text-gray-300 mb-6">
                Most software goes wrong at the estimate, not the build. We scope in writing
                first &mdash; what it does, what it costs, how long it takes &mdash; so you can
                decide with real numbers instead of a brochure.
              </p>

              <ul className="space-y-3">
                {['Fixed written scope before any code', 'Working software you can use in stages', 'Yours to own, host and move'].map((item) => (
                  <li key={item} className="flex items-center text-white">
                    <CheckCircle className="w-5 h-5 text-[#FF6B00] mr-3 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex justify-center">
              <img
                src="/images/home/hero-data-center.jpg"
                alt=""
                loading="lazy"
                className="w-full max-w-md rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Estimator CTA */}
      <section className="py-16 bg-gradient-to-r from-[#FF6B00] to-[#ff8124]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1a1c1b] mb-6">
            Not sure what your project should cost? Get a range in about two minutes.
          </h2>
          <a
            href="#estimator"
            className="inline-block px-8 py-4 bg-[#1a1c1b] text-white font-bold rounded-full text-lg hover:bg-black transition-colors shadow-lg"
            data-testid="estimator-cta"
          >
            Estimate my project
          </a>
        </div>
      </section>

      {/* How we build */}
      <section className="py-20 bg-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-white text-center mb-12">
            How we build
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {TECH_FEATURES.map((feature, index) => (
              <div key={index} className="bg-bots-surface/50 p-6 rounded-xl border border-[#4a3520]">
                <h4 className="text-[#FF6B00] font-bold mb-2">{feature.title}</h4>
                <p className="text-gray-300 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client testimonials */}
      <section className="py-20 bg-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block px-3 py-1 rounded-full border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-semibold tracking-wide mb-4">
              CLIENT TESTIMONIALS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
            <p className="text-[#888680]">
              Don&apos;t just take our word for it. Here&apos;s what businesses across the
              country have to say about working with us.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-bots-surface/50 p-6 rounded-2xl border border-[#4a3520] flex flex-col">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#FF6B00] text-[#FF6B00]" />
                  ))}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-6 flex-1">&ldquo;{t.content}&rdquo;</p>
                <div>
                  <p className="text-white font-semibold">{t.name}</p>
                  <p className="text-[#888680] text-xs">{t.role}, {t.company}</p>
                  <p className="text-[#888680] text-xs">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-r from-[#4a3520]/30 to-bots-dark">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <span className="inline-block px-3 py-1 rounded-full border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-semibold tracking-wide mb-4">
                COMMON QUESTIONS
              </span>
              <h2 className="text-3xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-300 mb-6">
                Got questions? We&apos;ve got answers. If you don&apos;t find what you&apos;re
                looking for, feel free to reach out to us directly.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 px-6 py-3 bg-[#FF6B00] hover:bg-[#ff8124] text-[#1a1c1b] font-bold rounded-full transition-colors"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="md:col-span-2 space-y-3">
              {FAQS.map((faq, index) => {
                const isOpen = openFaq === index;
                return (
                  <div key={faq.question} className="bg-bots-surface/50 border border-[#4a3520] rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                      aria-expanded={isOpen}
                    >
                      <span className="text-white font-medium">{faq.question}</span>
                      <ChevronDown className={`w-5 h-5 text-[#FF6B00] shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <p className="px-5 pb-4 text-gray-300 text-sm leading-relaxed">{faq.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Demo Form Section */}
      <section id="schedule-demo" className="py-20 bg-gradient-to-b from-bots-dark to-bots-surface">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready To Explore a New Way of Working?
              </h2>
              <p className="text-gray-300 text-lg mb-8">
                Tell us what you are trying to build or fix. We will come back with an honest
                view of scope, a cost range and a timeline &mdash; and say so if we think you do
                not need custom software at all.
              </p>
            </div>
            
            <div className="bg-bots-surface p-8 rounded-2xl border border-blue-500/20">
              <DemoRequestForm />
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            <span className="text-blue-400">INDUSTRIES</span> WE SERVE... AND MANY MORE...
          </h2>
          
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {INDUSTRIES.map((industry, index) => (
              <div 
                key={industry.name}
                className="text-center group animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="w-20 h-20 mx-auto mb-3 flex items-center justify-center bg-bots-surface/50 rounded-xl group-hover:bg-blue-500/20 transition-colors">
                  <img 
                    src={industry.icon} 
                    alt={industry.name}
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <p className="text-white text-sm font-medium">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-12 bg-bots-surface">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-white mb-6">
            <span className="text-blue-400">AVAILABLE STATES</span> WE SERVE
          </h3>
          <p className="text-gray-300 leading-relaxed">
            Missouri | Iowa | Illinois | Indiana | Ohio | Kentucky | Tennessee | Arkansas | Kansas | Oklahoma
          </p>
          <p className="text-gray-300 leading-relaxed mt-2">
            Texas | Louisiana | Mississippi | Alabama | Georgia | South Carolina | Florida | Puerto Rico | Virgin Islands
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Demo Request Form Component
const DemoRequestForm = () => {
  const [formData, setFormData] = useState({
    organization: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    product: '',
    contactMethod: 'both',
    notes: '',
    agreedToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          subject: `Demo Request - ${formData.organization}`,
          message: `Organization: ${formData.organization}\nProduct Interest: ${formData.product}\nContact Method: ${formData.contactMethod}\nAdditional Notes: ${formData.notes}`,
          source: 'demo_request',
        }),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          organization: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          product: '',
          contactMethod: 'both',
          notes: '',
          agreedToTerms: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
        <p className="text-gray-300">We'll be in touch shortly to schedule your demo.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-request-form">
      <input
        type="text"
        placeholder="Organization*"
        required
        value={formData.organization}
        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
        className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#FF6B00] focus:outline-none"
        data-testid="demo-form-organization"
      />
      
      <div className="grid grid-cols-2 gap-4">
        <input
          type="text"
          placeholder="First Name*"
          required
          value={formData.firstName}
          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
          className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#FF6B00] focus:outline-none"
          data-testid="demo-form-firstname"
        />
        <input
          type="text"
          placeholder="Last Name*"
          required
          value={formData.lastName}
          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
          className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#FF6B00] focus:outline-none"
          data-testid="demo-form-lastname"
        />
      </div>
      
      <select
        required
        value={formData.product}
        onChange={(e) => setFormData({ ...formData, product: e.target.value })}
        className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white focus:border-[#FF6B00] focus:outline-none"
        data-testid="demo-form-product"
      >
        <option value="">What do you need?*</option>
        <option value="Custom platform">Custom platform or internal system</option>
        <option value="Website">Website or web app</option>
        <option value="Integration">Integration between existing tools</option>
        <option value="Automation">Automating a manual process</option>
        <option value="Support">Support or changes to existing software</option>
        <option value="Not sure">Not sure yet</option>
      </select>
      
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center text-white cursor-pointer">
          <input
            type="radio"
            name="contactMethod"
            value="phone"
            checked={formData.contactMethod === 'phone'}
            onChange={(e) => setFormData({ ...formData, contactMethod: e.target.value })}
            className="mr-2"
          />
          Phone
        </label>
        <label className="flex items-center text-white cursor-pointer">
          <input
            type="radio"
            name="contactMethod"
            value="email"
            checked={formData.contactMethod === 'email'}
            onChange={(e) => setFormData({ ...formData, contactMethod: e.target.value })}
            className="mr-2"
          />
          Email
        </label>
        <label className="flex items-center text-white cursor-pointer">
          <input
            type="radio"
            name="contactMethod"
            value="both"
            checked={formData.contactMethod === 'both'}
            onChange={(e) => setFormData({ ...formData, contactMethod: e.target.value })}
            className="mr-2"
          />
          Both
        </label>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <input
          type="email"
          placeholder="Email*"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#FF6B00] focus:outline-none"
          data-testid="demo-form-email"
        />
        <input
          type="tel"
          placeholder="Phone*"
          required
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#FF6B00] focus:outline-none"
          data-testid="demo-form-phone"
        />
      </div>
      
      <textarea
        placeholder="Enter any additional notes or more information"
        value={formData.notes}
        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        rows={3}
        className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#FF6B00] focus:outline-none resize-none"
        data-testid="demo-form-notes"
      />
      
      <label className="flex items-start text-gray-300 text-sm cursor-pointer">
        <input
          type="checkbox"
          required
          checked={formData.agreedToTerms}
          onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
          className="mr-3 mt-1"
          data-testid="demo-form-terms"
        />
        <span>
          I agree to the terms &amp; conditions. By providing my phone number,
          I agree to receive text messages from the business.
        </span>
      </label>
      
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-4 bg-green-500 text-black font-bold rounded-full hover:bg-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="demo-form-submit"
      >
        {isSubmitting ? 'Submitting...' : 'SCHEDULE MY CALL'}
      </button>
      
      {submitStatus === 'error' && (
        <p className="text-red-400 text-center">Something went wrong. Please try again.</p>
      )}
    </form>
  );
};

export default HomePage;
